import { Suspense, lazy, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Lock, Map, Users, Search, X, Building2, Plus } from 'lucide-react';
import { AppHeader } from '@/components/layout/AppHeader';
import { Footer } from '@/components/layout/Footer';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent } from '@/components/ui/card';
import { ProfileCard } from '@/components/profile/ProfileCard';
import { BusinessCard } from '@/components/business/BusinessCard';
import { INDUSTRIES, BUSINESS_CATEGORIES, COUNTRIES } from '@/lib/constants';
import { useDebouncedValue } from '@/lib/hooks/useDebouncedValue';
import { useDistinctCountries, useProfiles, useMapProfiles } from '@/lib/hooks/useProfiles';
import { useBusinesses, useMapBusinesses } from '@/lib/hooks/useBusinesses';
import { createBusiness, slugifyBusinessName } from '@/lib/api/businesses';
import { useAuth } from '@/lib/hooks/useAuth';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { VerificationFilter } from '@/lib/api/profiles';
import { cn } from '@/lib/utils/cn';

const MemberMapView = lazy(() =>
  import('@/components/map/MemberMapView').then((m) => ({
    default: m.MemberMapView,
  })),
);

function toggle<T>(list: T[], item: T): T[] {
  return list.includes(item) ? list.filter((v) => v !== item) : [...list, item];
}

type Chip = {
  label: string;
  active: boolean;
  onClick: () => void;
};

function ChipRow({ chips }: { chips: Chip[] }) {
  return (
    <div className="flex flex-wrap gap-2">
      {chips.map((c) => (
        <button
          key={c.label}
          type="button"
          onClick={c.onClick}
          aria-pressed={c.active}
          className={cn(
            'rounded-full border px-3 py-1.5 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold/40',
            c.active
              ? 'border-gold bg-gold/15 text-gold-dark'
              : 'border-border bg-white text-navy/70 hover:border-gold/40 hover:text-navy',
          )}
        >
          {c.label}
        </button>
      ))}
    </div>
  );
}

const VERIFICATION_OPTIONS: { value: VerificationFilter; label: string }[] = [
  { value: 'all', label: 'All members' },
  { value: 'members_only', label: 'Members' },
  { value: 'verified_only', label: 'Verified' },
];

function TabButton({
  active,
  onClick,
  icon,
  children,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-selected={active}
      role="tab"
      className={cn(
        'flex items-center gap-2 border-b-2 px-4 py-3 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold/40',
        active
          ? 'border-gold text-navy'
          : 'border-transparent text-muted hover:text-navy',
      )}
    >
      <span className="h-4 w-4" aria-hidden="true">
        {icon}
      </span>
      {children}
    </button>
  );
}

// ── Business create form schema ───────────────────────────────────────────────

const bizSchema = z.object({
  name: z.string().min(2).max(200),
  slug: z.string().min(2).max(160).regex(/^[a-z0-9-]+$/, 'Only lowercase letters, numbers, and hyphens'),
  tagline: z.string().max(300).optional(),
  description: z.string().max(5000).optional(),
  category: z.enum(BUSINESS_CATEGORIES),
  industry: z.string().optional(),
  city: z.string().optional(),
  country: z.string().optional(),
  website: z.string().url('Enter a full URL e.g. https://example.com').optional().or(z.literal('')),
  phone: z.string().optional(),
  address: z.string().optional(),
});
type BizFormValues = z.infer<typeof bizSchema>;

// ── Component ─────────────────────────────────────────────────────────────────

export function Directory() {
  const [searchParams, setSearchParams] = useSearchParams();
  const rawTab = searchParams.get('tab');
  const tab: 'map' | 'members' | 'businesses' =
    rawTab === 'members' ? 'members' : rawTab === 'businesses' ? 'businesses' : 'map';

  const setTab = (t: 'map' | 'members' | 'businesses') => {
    setSearchParams(t === 'map' ? {} : { tab: t }, { replace: true });
  };

  // Map data
  const { data: mapData, isLoading: mapLoading, isError: mapIsError, error: mapError } =
    useMapProfiles();
  const { data: mapBizData } = useMapBusinesses();
  const { profile, isAuthenticated } = useAuth();
  const mapProfiles = mapData ?? [];
  const mapBusinesses = mapBizData ?? [];
  const [showMembers, setShowMembers] = useState(true);
  const [showBusinesses, setShowBusinesses] = useState(true);
  const navigate = useNavigate();
  const qc = useQueryClient();

  // ── Members filter state ───────────────────────────────────────────────────
  const [searchInput, setSearchInput] = useState('');
  const search = useDebouncedValue(searchInput, 300);
  const [industries, setIndustries] = useState<string[]>([]);
  const [countries, setCountries] = useState<string[]>([]);
  const [verification, setVerification] = useState<VerificationFilter>('all');

  const filters = useMemo(
    () => ({ search, industries, countries, verification }),
    [search, industries, countries, verification],
  );

  const {
    data,
    isLoading,
    isError,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useProfiles(filters);
  const countriesQuery = useDistinctCountries();

  const items = data?.pages.flatMap((p) => p.items) ?? [];
  const total = items.length;
  const hasAnyFilter =
    !!search || industries.length > 0 || countries.length > 0 || verification !== 'all';

  const sentinelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && hasNextPage && !isFetchingNextPage) {
          void fetchNextPage();
        }
      },
      { rootMargin: '200px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  const clearFilters = () => {
    setSearchInput('');
    setIndustries([]);
    setCountries([]);
    setVerification('all');
  };

  // ── Businesses filter + create state ──────────────────────────────────────
  const [bizSearchInput, setBizSearchInput] = useState('');
  const bizSearch = useDebouncedValue(bizSearchInput, 300);
  const [bizCategories, setBizCategories] = useState<string[]>([]);
  const [bizCountries, setBizCountries] = useState<string[]>([]);
  const [showBizCreate, setShowBizCreate] = useState(false);

  const bizFilter = useMemo(
    () => ({ search: bizSearch, categories: bizCategories, countries: bizCountries }),
    [bizSearch, bizCategories, bizCountries],
  );

  const {
    data: bizData,
    isLoading: bizLoading,
    isError: bizIsError,
    error: bizError,
    fetchNextPage: bizFetchNext,
    hasNextPage: bizHasMore,
    isFetchingNextPage: bizFetchingMore,
  } = useBusinesses(bizFilter);

  const bizItems = useMemo(() => bizData?.pages.flatMap((p) => p.items) ?? [], [bizData]);
  const hasBizFilter = !!bizSearch || bizCategories.length > 0 || bizCountries.length > 0;

  const bizSentinelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = bizSentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && bizHasMore && !bizFetchingMore) {
          void bizFetchNext();
        }
      },
      { rootMargin: '200px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [bizFetchNext, bizHasMore, bizFetchingMore]);

  const clearBizFilters = () => {
    setBizSearchInput('');
    setBizCategories([]);
    setBizCountries([]);
  };

  // Business create form
  const {
    register: bizRegister,
    handleSubmit: bizHandleSubmit,
    watch: bizWatch,
    setValue: bizSetValue,
    formState: { errors: bizErrors },
    reset: bizReset,
  } = useForm<BizFormValues>({
    resolver: zodResolver(bizSchema),
    defaultValues: { category: 'Other' },
  });

  const bizNameVal = bizWatch('name');

  const { mutate: doCreateBusiness, isPending: creatingBiz, error: bizCreateError } = useMutation({
    mutationFn: (values: BizFormValues) =>
      createBusiness({
        name: values.name,
        slug: values.slug,
        tagline: values.tagline,
        description: values.description,
        category: values.category,
        industry: values.industry,
        city: values.city,
        country: values.country,
        website: values.website || undefined,
        phone: values.phone,
        address: values.address,
        owner_profile_id: profile!.id,
      }),
    onSuccess: (slug) => {
      qc.invalidateQueries({ queryKey: ['businesses'] });
      bizReset();
      setShowBizCreate(false);
      navigate(`/businesses/${slug}`);
    },
  });

  const onBizNameBlur = () => {
    const slug = bizWatch('slug');
    if (!slug && bizNameVal) {
      bizSetValue('slug', slugifyBusinessName(bizNameVal));
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-cream">
      <AppHeader />

      {/* Tab bar */}
      <div
        className="border-b border-border/60 bg-white"
        role="tablist"
        aria-label="Directory view"
      >
        <div className="container flex gap-0">
          <TabButton active={tab === 'map'} onClick={() => setTab('map')} icon={<Map />}>
            Map
          </TabButton>
          <TabButton active={tab === 'members'} onClick={() => setTab('members')} icon={<Users />}>
            Members
          </TabButton>
          <TabButton active={tab === 'businesses'} onClick={() => setTab('businesses')} icon={<Building2 />}>
            Businesses
          </TabButton>
        </div>
      </div>

      <main className="flex flex-1 flex-col" role="tabpanel">
        {/* ── Map tab ─────────────────────────────────────────────────────── */}
        {tab === 'map' && (
          <>
            {mapLoading && (
              <div className="flex h-[calc(100vh-7rem-2.5rem)] items-center justify-center">
                <Skeleton className="h-full w-full" />
              </div>
            )}

            {mapIsError && (
              <div className="flex h-[calc(100vh-7rem-2.5rem)] items-center justify-center">
                <p role="alert" className="max-w-md text-center text-sm text-destructive">
                  Couldn't load the map
                  {mapError && typeof mapError === 'object' && 'message' in mapError
                    ? `: ${(mapError as { message: string }).message}`
                    : '.'}
                </p>
              </div>
            )}

            {!mapLoading && !mapIsError && (
              <>
                <div className="relative h-[calc(100vh-7rem-2.5rem)] w-full">
                  <div
                    className="absolute right-4 top-4 z-[500] rounded-xl border border-border/70 bg-white/95 px-4 py-3 shadow-md backdrop-blur"
                    aria-label="Map layer controls"
                  >
                    <label className="flex cursor-pointer select-none items-center gap-2 font-display text-sm text-navy">
                      <input
                        type="checkbox"
                        checked={showMembers}
                        onChange={(e) => setShowMembers(e.target.checked)}
                        className="h-3.5 w-3.5 cursor-pointer"
                      />
                      <span className="inline-block h-2 w-2 shrink-0 rounded-full bg-gold" aria-hidden="true" />
                      {mapProfiles.length} members
                    </label>
                    {mapBusinesses.length > 0 && (
                      <label className="mt-1.5 flex cursor-pointer select-none items-center gap-2 font-display text-sm text-navy">
                        <input
                          type="checkbox"
                          checked={showBusinesses}
                          onChange={(e) => setShowBusinesses(e.target.checked)}
                          className="h-3.5 w-3.5 cursor-pointer"
                        />
                        <span className="inline-block h-2 w-2 shrink-0 rounded-full bg-navy" aria-hidden="true" />
                        {mapBusinesses.length} {mapBusinesses.length === 1 ? 'business' : 'businesses'}
                      </label>
                    )}
                    {!isAuthenticated && (
                      <p className="mt-2 flex items-center gap-1.5 text-xs text-muted">
                        <Lock className="h-3 w-3 shrink-0" aria-hidden="true" />
                        Sign in to view profiles.
                      </p>
                    )}
                  </div>

                  <Suspense fallback={<Skeleton className="h-full w-full" />}>
                    <MemberMapView
                      profiles={showMembers ? mapProfiles : []}
                      businesses={showBusinesses ? mapBusinesses : []}
                    />
                  </Suspense>
                </div>
              </>
            )}
          </>
        )}

        {/* ── Members tab ─────────────────────────────────────────────────── */}
        {tab === 'members' && (
          <>
            <section className="border-b border-border/60 bg-cream-warm/40">
              <div className="max-w-4xl mx-auto px-6 py-10">
                <h1 className="font-display text-3xl text-navy sm:text-4xl">
                  Member directory
                </h1>
                <p className="mt-2 max-w-2xl text-sm text-muted">
                  Search 345 seeded members across 13 countries. Full profiles are
                  visible to signed-in members.
                </p>

                <div className="mt-6 flex flex-col gap-5">
                  <div className="relative max-w-xl">
                    <Search
                      aria-hidden="true"
                      className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted"
                    />
                    <Input
                      type="search"
                      value={searchInput}
                      onChange={(e) => setSearchInput(e.target.value)}
                      placeholder="Search by name, role, or city"
                      aria-label="Search members"
                      className="pl-9"
                    />
                  </div>

                  <div className="flex flex-col gap-2">
                    <p className="text-xs font-medium uppercase tracking-widest text-muted">
                      Industry
                    </p>
                    <ChipRow
                      chips={INDUSTRIES.map((ind) => ({
                        label: ind,
                        active: industries.includes(ind),
                        onClick: () => setIndustries((prev) => toggle(prev, ind)),
                      }))}
                    />
                  </div>

                  <div className="flex flex-col gap-2">
                    <p className="text-xs font-medium uppercase tracking-widest text-muted">
                      Country
                    </p>
                    {countriesQuery.isLoading ? (
                      <div className="flex gap-2">
                        {[0, 1, 2, 3, 4].map((i) => (
                          <Skeleton key={i} className="h-7 w-20" />
                        ))}
                      </div>
                    ) : (
                      <ChipRow
                        chips={(countriesQuery.data ?? []).map((c) => ({
                          label: c,
                          active: countries.includes(c),
                          onClick: () => setCountries((prev) => toggle(prev, c)),
                        }))}
                      />
                    )}
                  </div>

                  <div className="flex flex-col gap-2">
                    <p className="text-xs font-medium uppercase tracking-widest text-muted">
                      Status
                    </p>
                    <ChipRow
                      chips={VERIFICATION_OPTIONS.map((opt) => ({
                        label: opt.label,
                        active: verification === opt.value,
                        onClick: () => setVerification(opt.value),
                      }))}
                    />
                  </div>

                  {hasAnyFilter && (
                    <div>
                      <Button variant="ghost-dark" size="sm" onClick={clearFilters}>
                        <X className="mr-1 h-3.5 w-3.5" />
                        Clear filters
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </section>

            <section className="max-w-4xl mx-auto px-6 py-10">
              {isError ? (
                <p role="alert" className="text-sm text-destructive">
                  Couldn't load the directory
                  {error instanceof Error ? `: ${error.message}` : '.'}
                </p>
              ) : isLoading ? (
                <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                  {Array.from({ length: 8 }).map((_, i) => (
                    <Skeleton key={i} className="h-44 rounded-2xl" />
                  ))}
                </div>
              ) : items.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-border bg-white p-10 text-center">
                  <p className="font-display text-lg text-navy">
                    No members match these filters.
                  </p>
                  <p className="mt-2 text-sm text-muted">
                    Try broadening your search or clearing filters.
                  </p>
                </div>
              ) : (
                <>
                  <p className="mb-4 text-xs uppercase tracking-widest text-muted">
                    Showing {total} member{total === 1 ? '' : 's'}
                    {hasNextPage ? ' (more below)' : ''}
                  </p>
                  <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                    {items.map((profile) => (
                      <ProfileCard key={profile.id} profile={profile} />
                    ))}
                  </div>
                  <div ref={sentinelRef} aria-hidden="true" className="h-10 w-full" />
                  {isFetchingNextPage && (
                    <p className="mt-6 text-center text-sm text-muted">
                      Loading more…
                    </p>
                  )}
                </>
              )}
            </section>

            <Footer />
          </>
        )}

        {/* ── Businesses tab ──────────────────────────────────────────────── */}
        {tab === 'businesses' && (
          <>
            <section className="border-b border-border/60 bg-cream-warm/40">
              <div className="max-w-4xl mx-auto px-6 py-10">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h1 className="font-display text-3xl text-navy sm:text-4xl">
                      Business directory
                    </h1>
                    <p className="mt-2 max-w-2xl text-sm text-muted">
                      Discover Cambodian-owned businesses around the world.
                    </p>
                  </div>
                  {isAuthenticated && (
                    <Button
                      className="shrink-0 bg-navy text-cream hover:bg-navy-deep gap-2"
                      onClick={() => setShowBizCreate(true)}
                    >
                      <Plus className="h-4 w-4" aria-hidden="true" />
                      Add a business
                    </Button>
                  )}
                </div>

                <div className="mt-6 flex flex-col gap-5">
                  <div className="relative max-w-xl">
                    <Search
                      aria-hidden="true"
                      className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted"
                    />
                    <Input
                      type="search"
                      value={bizSearchInput}
                      onChange={(e) => setBizSearchInput(e.target.value)}
                      placeholder="Search by name, tagline, or city"
                      aria-label="Search businesses"
                      className="pl-9"
                    />
                  </div>

                  <div className="flex flex-col gap-2">
                    <p className="text-xs font-medium uppercase tracking-widest text-muted">
                      Category
                    </p>
                    <ChipRow
                      chips={BUSINESS_CATEGORIES.map((cat) => ({
                        label: cat,
                        active: bizCategories.includes(cat),
                        onClick: () => setBizCategories((prev) => toggle(prev, cat)),
                      }))}
                    />
                  </div>

                  <div className="flex flex-col gap-2">
                    <p className="text-xs font-medium uppercase tracking-widest text-muted">
                      Country
                    </p>
                    <ChipRow
                      chips={COUNTRIES.map((c) => ({
                        label: c,
                        active: bizCountries.includes(c),
                        onClick: () => setBizCountries((prev) => toggle(prev, c)),
                      }))}
                    />
                  </div>

                  {hasBizFilter && (
                    <div>
                      <Button variant="ghost-dark" size="sm" onClick={clearBizFilters}>
                        <X className="mr-1 h-3.5 w-3.5" />
                        Clear filters
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </section>

            <section className="max-w-4xl mx-auto px-6 py-10">
              {/* Create form */}
              {showBizCreate && isAuthenticated && (
                <Card className="mb-6 border-gold/30">
                  <CardContent className="pt-6">
                    <h2 className="font-semibold text-navy text-lg mb-4">Add a business</h2>
                    <form onSubmit={bizHandleSubmit((v) => doCreateBusiness(v))} className="space-y-4">
                      <div className="grid sm:grid-cols-2 gap-4">
                        <div className="space-y-1.5">
                          <Label htmlFor="biz-name">Business name</Label>
                          <Input
                            id="biz-name"
                            {...bizRegister('name')}
                            onBlur={onBizNameBlur}
                            placeholder="Phnom Penh Kitchen"
                          />
                          {bizErrors.name && (
                            <p className="text-xs text-red-500">{bizErrors.name.message}</p>
                          )}
                        </div>
                        <div className="space-y-1.5">
                          <Label htmlFor="biz-slug">URL slug</Label>
                          <Input
                            id="biz-slug"
                            {...bizRegister('slug')}
                            placeholder="phnom-penh-kitchen"
                          />
                          {bizErrors.slug && (
                            <p className="text-xs text-red-500">{bizErrors.slug.message}</p>
                          )}
                        </div>
                      </div>

                      <div className="space-y-1.5">
                        <Label htmlFor="biz-tagline">Tagline <span className="text-muted font-normal">(optional)</span></Label>
                        <Input
                          id="biz-tagline"
                          {...bizRegister('tagline')}
                          placeholder="Authentic Khmer cuisine in the heart of Long Beach"
                        />
                      </div>

                      <div className="space-y-1.5">
                        <Label htmlFor="biz-description">Description <span className="text-muted font-normal">(optional)</span></Label>
                        <textarea
                          id="biz-description"
                          {...bizRegister('description')}
                          rows={3}
                          className="w-full rounded-md border border-muted/30 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gold/50 resize-none"
                          placeholder="Tell people about this business…"
                        />
                      </div>

                      <div className="grid sm:grid-cols-2 gap-4">
                        <div className="space-y-1.5">
                          <Label htmlFor="biz-category">Category</Label>
                          <select
                            id="biz-category"
                            {...bizRegister('category')}
                            className="w-full rounded-md border border-muted/30 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gold/50 bg-white"
                          >
                            {BUSINESS_CATEGORIES.map((cat) => (
                              <option key={cat} value={cat}>{cat}</option>
                            ))}
                          </select>
                        </div>
                        <div className="space-y-1.5">
                          <Label htmlFor="biz-industry">Industry <span className="text-muted font-normal">(optional)</span></Label>
                          <select
                            id="biz-industry"
                            {...bizRegister('industry')}
                            className="w-full rounded-md border border-muted/30 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gold/50 bg-white"
                          >
                            <option value="">Select industry</option>
                            {INDUSTRIES.map((ind) => (
                              <option key={ind} value={ind}>{ind}</option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div className="grid sm:grid-cols-2 gap-4">
                        <div className="space-y-1.5">
                          <Label htmlFor="biz-city">City <span className="text-muted font-normal">(optional)</span></Label>
                          <Input id="biz-city" {...bizRegister('city')} placeholder="Long Beach" />
                        </div>
                        <div className="space-y-1.5">
                          <Label htmlFor="biz-country">Country <span className="text-muted font-normal">(optional)</span></Label>
                          <select
                            id="biz-country"
                            {...bizRegister('country')}
                            className="w-full rounded-md border border-muted/30 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gold/50 bg-white"
                          >
                            <option value="">Select country</option>
                            {COUNTRIES.map((c) => (
                              <option key={c} value={c}>{c}</option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div className="grid sm:grid-cols-2 gap-4">
                        <div className="space-y-1.5">
                          <Label htmlFor="biz-website">Website <span className="text-muted font-normal">(optional)</span></Label>
                          <Input
                            id="biz-website"
                            {...bizRegister('website')}
                            type="url"
                            placeholder="https://example.com"
                          />
                          {bizErrors.website && (
                            <p className="text-xs text-red-500">{bizErrors.website.message}</p>
                          )}
                        </div>
                        <div className="space-y-1.5">
                          <Label htmlFor="biz-phone">Phone <span className="text-muted font-normal">(optional)</span></Label>
                          <Input id="biz-phone" {...bizRegister('phone')} placeholder="+1 562 555 0100" />
                        </div>
                      </div>

                      <div className="space-y-1.5">
                        <Label htmlFor="biz-address">Address <span className="text-muted font-normal">(optional)</span></Label>
                        <Input
                          id="biz-address"
                          {...bizRegister('address')}
                          placeholder="123 Main St, Long Beach, CA 90802"
                        />
                      </div>

                      {bizCreateError && (
                        <p className="text-sm text-red-600">
                          {bizCreateError instanceof Error
                            ? bizCreateError.message
                            : 'Failed to add business. Please try again.'}
                        </p>
                      )}

                      <div className="flex gap-2 justify-end">
                        <Button
                          variant="ghost"
                          type="button"
                          onClick={() => { setShowBizCreate(false); bizReset(); }}
                        >
                          Cancel
                        </Button>
                        <Button
                          type="submit"
                          className="bg-navy text-cream hover:bg-navy-deep"
                          disabled={creatingBiz}
                        >
                          {creatingBiz ? 'Adding…' : 'Add business'}
                        </Button>
                      </div>
                    </form>
                  </CardContent>
                </Card>
              )}

              {/* Grid */}
              {bizIsError ? (
                <p role="alert" className="text-sm text-destructive">
                  Couldn't load businesses
                  {bizError instanceof Error ? `: ${bizError.message}` : '.'}
                </p>
              ) : bizLoading ? (
                <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                  {Array.from({ length: 8 }).map((_, i) => (
                    <Skeleton key={i} className="h-40 rounded-2xl" />
                  ))}
                </div>
              ) : bizItems.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-border bg-white p-10 text-center">
                  <Building2 className="mx-auto h-10 w-10 mb-3 text-muted/30" aria-hidden="true" />
                  <p className="font-display text-lg text-navy">No businesses listed yet.</p>
                  {isAuthenticated && (
                    <p className="mt-2 text-sm text-muted">
                      Be the first to{' '}
                      <button
                        onClick={() => setShowBizCreate(true)}
                        className="text-navy underline hover:text-navy-deep focus:outline-none"
                      >
                        add a business
                      </button>
                      .
                    </p>
                  )}
                </div>
              ) : (
                <>
                  <p className="mb-4 text-xs uppercase tracking-widest text-muted">
                    {bizItems.length} business{bizItems.length === 1 ? '' : 'es'}
                    {bizHasMore ? ' (more below)' : ''}
                  </p>
                  <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                    {bizItems.map((biz) => (
                      <BusinessCard key={biz.id} business={biz} />
                    ))}
                  </div>
                  <div ref={bizSentinelRef} aria-hidden="true" className="h-10 w-full" />
                  {bizFetchingMore && (
                    <p className="mt-6 text-center text-sm text-muted">Loading more…</p>
                  )}
                </>
              )}
            </section>

            <Footer />
          </>
        )}
      </main>
    </div>
  );
}
